import { useElementSize } from '@mantine/hooks'
import { Box, Stack, useTheme } from '@mui/material'
import { Suspense } from 'react'
import { useLocation } from 'react-router-dom'

import LoadingPaperSkeleton from '~/components/loadingPaperSkeleton'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import { EAgentKeys } from '~/features/agents/models'
import Header from '~/layouts/account/header'
import EAccountGridAreas from '~/layouts/account/models'
import ContactInfoButton from '~/layouts/app/header/account/portal/contactInfoButton'
import PageLayout from '~/layouts/page'
import { EClientPath } from '~/models/api'
import rem from '~/utils/ui'

type TProps = {
  children: React.ReactNode
  contentHeader?: React.ReactNode
  isLoading?: boolean
  noPadding?: boolean
  showContactInfoButton?: boolean
  fullWidth?: boolean
}

function ContentWrapper({
  children,
  contentHeader = null,
  isLoading = false,
  noPadding = false,
  showContactInfoButton = false,
  fullWidth = false,
}: TProps) {
  const location = useLocation()
  const { ref: headerRef } = useElementSize()
  const theme = useTheme()
  const { isError, selectedAgent: agent } = useGetAgentCentralized()

  // Only show contact info button if there's an agent
  const hasAgent = !isError && !!agent && !!agent[EAgentKeys.id]
  const shouldShowMobileContactInfo = showContactInfoButton && hasAgent

  const loadingTemplate = (
    <>
      {[
        EClientPath.reports,
        EClientPath.contacts,
        EClientPath.referralsInfo,
        EClientPath.integrations,
      ].some((path) => location.pathname.includes(path)) && (
        <LoadingPaperSkeleton
          sx={{
            height: '3rem',
            ':not(style):not(style)': {
              mb: '2rem',
            },
          }}
        />
      )}
      {!location.pathname.includes(EClientPath.inbox) && (
        <LoadingPaperSkeleton
          sx={{
            height: '70vh',
            maxWidth: rem(1200),
            ':not(style):not(style)': {
              margin: 'auto',
            },
          }}
        />
      )}
      {location.pathname.includes(EClientPath.inbox) && (
        <LoadingPaperSkeleton sx={{ height: '100%' }} />
      )}
    </>
  )

  return (
    <Stack
      spacing={0}
      sx={{
        display: 'grid',
        gridTemplateAreas: `'${EAccountGridAreas.HEADER} ${EAccountGridAreas.HEADER}' '${EAccountGridAreas.NAV} ${EAccountGridAreas.CONTENT}'`,
        minHeight: '100dvh',
        maxHeight: '100dvh',
        overflow: 'hidden',
        gridTemplateColumns: 'auto 1fr',
        gridTemplateRows: 'auto 1fr',
        backgroundColor: theme.palette.mainBackground,
      }}
    >
      <Header ref={headerRef} showContactInfoButton={showContactInfoButton} />
      <Stack
        sx={{
          gridArea: EAccountGridAreas.CONTENT,
          minHeight: rem(1),
          maxWidth: { xxxs: '100vw', md: 'none' },
          overflow: 'hidden',
        }}
      >
        {shouldShowMobileContactInfo && (
          <Box
            sx={{
              display: { xxxs: 'block', md: 'none' },
              paddingX: rem(16),
              paddingY: rem(8),
              backgroundColor: theme.palette.menuBackground,
            }}
          >
            <ContactInfoButton />
          </Box>
        )}
        {contentHeader}
        <PageLayout
          id="content-wrapper-page-layout"
          containerProps={{
            sx: {
              minHeight: 'inherit',
              display: 'flex',
              alignItems: 'flex-start',
              height: noPadding ? '100%' : 'initial',
              paddingBottom: noPadding ? 0 : rem(55),
              overflow: {
                xxxs: noPadding ? 'hidden' : 'initial',
                md: 'initial',
              },
              '&.MuiContainer-root': {
                paddingX: noPadding ? '0' : undefined,
                maxWidth: noPadding || fullWidth ? 'unset' : undefined,
              },
            },
          }}
          sx={{
            minHeight: 'inherit',
            overflow: 'auto',
            height: '100%',
          }}
        >
          <Stack
            spacing={3}
            sx={{
              width: '100%',
              height: '100%',
              paddingBlock: noPadding ? 0 : { xxxs: 1, xs: 2, md: 4 },
              position: 'relative',
            }}
            useFlexGap
          >
            {isLoading ? (
              loadingTemplate
            ) : (
              <Suspense fallback={loadingTemplate}>{children}</Suspense>
            )}
          </Stack>
        </PageLayout>
      </Stack>
    </Stack>
  )
}

export default ContentWrapper
